import { interval, Observable, takeUntil } from 'rxjs';
import { UserRole } from '@app/core/auth/auth.repository';
import {
  DirectChatCategory,
  DirectChatCategoryId,
  DirectChatMessage,
  DirectChatRecipient,
  DirectChatService,
} from '@app/services/direct-chat.service';

export interface DirectChatDrawerHost {
  /** Emits when the owning layout is destroyed. */
  destroy$: Observable<unknown>;
  translate(key: string): string;
  /** True while the chat drawer is open on the chat tab. */
  isChatVisible(): boolean;
  messagesContainer(): HTMLElement | undefined;
}

const ROLE_LABEL_KEYS: Record<number, string> = {
  [UserRole.SuperUser]: 'chat.role.superuser',
  [UserRole.SysAdmin]: 'chat.role.sysadmin',
  [UserRole.CourseAdmin]: 'chat.role.courseadmin',
  [UserRole.Translator]: 'chat.role.translator',
  [UserRole.Supervisor]: 'chat.role.supervisor',
};

/**
 * Chat drawer state for staff: role switch -> recipient -> private 1:1 thread.
 * The "students" category only records the selection; the layout keeps serving
 * student threads through ChatService.
 */
export class DirectChatDrawer {
  categories: DirectChatCategory[] = [];
  activeCategory: DirectChatCategoryId;
  recipients: DirectChatRecipient[] = [];
  recipientsLoading = false;
  recipientsError = '';
  searchTerm = '';
  selected: DirectChatRecipient | null = null;
  messages: DirectChatMessage[] = [];
  messagesLoading = false;
  messagesError = '';
  hasMore = false;
  isLoadingMore = false;
  input = '';
  sendError = '';
  unreadTotal = 0;

  private page = 1;
  private readonly perPage = 20;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly api: DirectChatService, role: UserRole, private readonly host: DirectChatDrawerHost) {
    // Translators have no student thread, so they must never land on it.
    this.activeCategory = role === UserRole.Translator ? 'supervisors' : 'students';
  }

  /** Loads the categories allowed for this account; roles without direct chat keep the student list. */
  init(pollMs = 10000): void {
    this.api.getCategories().pipe(takeUntil(this.host.destroy$)).subscribe({
      next: (categories) => {
        this.applyCategories(categories);
        if (!categories.length) return;
        if (!categories.some((c) => c.id === this.activeCategory)) {
          this.selectCategory(categories[0].id);
        }
        interval(pollMs).pipe(takeUntil(this.host.destroy$)).subscribe(() => this.poll());
      },
      error: () => { this.categories = []; },
    });
  }

  selectCategory(category: DirectChatCategoryId): void {
    this.activeCategory = category;
    this.selected = null;
    this.recipients = [];
    this.searchTerm = '';
    if (category !== 'students') this.loadRecipients();
  }

  categoryUnread(category: DirectChatCategoryId): number {
    return this.categories.find((c) => c.id === category)?.unread || 0;
  }

  onSearch(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadRecipients(), 300);
  }

  loadRecipients(quiet = false): void {
    const category = this.activeCategory;
    if (category === 'students') return;
    if (!quiet) {
      this.recipientsLoading = true;
      this.recipientsError = '';
    }
    this.api.getRecipients(category, this.searchTerm.trim()).pipe(takeUntil(this.host.destroy$)).subscribe({
      next: (page) => {
        if (category !== this.activeCategory) return;
        this.recipients = page.data;
        this.recipientsLoading = false;
        this.recipientsError = '';
      },
      error: () => {
        if (category !== this.activeCategory || quiet) return;
        this.recipientsLoading = false;
        this.recipientsError = this.host.translate('chat.direct.loadError');
      },
    });
  }

  open(recipient: DirectChatRecipient): void {
    this.selected = recipient;
    this.messages = [];
    this.page = 1;
    this.hasMore = false;
    this.input = '';
    this.sendError = '';
    this.loadLatest(true);
  }

  back(): void {
    this.selected = null;
    this.loadRecipients(true);
  }

  loadMore(): void {
    const recipient = this.selected;
    if (!recipient || this.isLoadingMore || !this.hasMore) return;
    this.isLoadingMore = true;
    this.api.getMessages(recipient.account_id, this.page + 1, this.perPage).pipe(takeUntil(this.host.destroy$)).subscribe({
      next: (page) => {
        this.isLoadingMore = false;
        if (recipient !== this.selected) return;
        this.page = page.page;
        this.messages = this.merge(page.data);
        this.hasMore = page.page * page.per_page < page.total;
      },
      error: () => { this.isLoadingMore = false; },
    });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  send(): void {
    const recipient = this.selected;
    const text = this.input.trim();
    if (!recipient || !text) return;
    this.sendError = '';
    const pending: DirectChatMessage = {
      id: -Date.now(), sender_account_id: 0, message: text,
      created: new Date().toISOString(), read_at: null, is_mine: true,
    };
    this.messages = [...this.messages, pending];
    this.input = '';
    this.scrollToBottom();
    this.api.sendMessage(recipient.account_id, text).pipe(takeUntil(this.host.destroy$)).subscribe({
      next: (saved) => {
        if (recipient !== this.selected) return;
        this.messages = this.merge([saved], pending.id);
      },
      error: () => {
        if (recipient !== this.selected) return;
        this.messages = this.messages.filter((m) => m !== pending);
        if (!this.input) this.input = text;
        this.sendError = this.host.translate('chat.direct.sendError');
      },
    });
  }

  roleLabelKey(roleId: number): string {
    return ROLE_LABEL_KEYS[roleId] || 'chat.role.staff';
  }

  trackRecipient(index: number, recipient: DirectChatRecipient): number {
    return recipient.account_id;
  }

  trackMessage(index: number, message: DirectChatMessage): number {
    return message.id;
  }

  poll(): void {
    if (document.hidden) return;
    this.refreshUnread();
    if (!this.host.isChatVisible() || this.activeCategory === 'students') return;
    if (this.selected) {
      this.loadLatest(false);
    } else {
      this.loadRecipients(true);
    }
  }

  private loadLatest(initial: boolean): void {
    const recipient = this.selected;
    if (!recipient) return;
    if (initial) {
      this.messagesLoading = true;
      this.messagesError = '';
    }
    const el = this.host.messagesContainer();
    const atBottom = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    this.api.getMessages(recipient.account_id, 1, this.perPage).pipe(takeUntil(this.host.destroy$)).subscribe({
      next: (page) => {
        if (recipient !== this.selected) return;
        const before = this.messages.length;
        this.messages = this.merge(page.data);
        if (initial) this.hasMore = page.page * page.per_page < page.total;
        this.messagesLoading = false;
        this.messagesError = '';
        if (initial || (atBottom && this.messages.length > before)) this.scrollToBottom();
        if (page.data.some((m) => !m.is_mine && !m.read_at)) this.markRead(recipient);
      },
      error: () => {
        if (recipient !== this.selected) return;
        this.messagesLoading = false;
        if (initial) this.messagesError = this.host.translate('chat.direct.loadError');
      },
    });
  }

  /** Merges by id so polling and "earlier messages" never duplicate or drop rows. */
  private merge(incoming: DirectChatMessage[], replaceId?: number): DirectChatMessage[] {
    const byId = new Map<number, DirectChatMessage>();
    this.messages.filter((m) => m.id !== replaceId).forEach((m) => byId.set(m.id, m));
    incoming.forEach((m) => byId.set(m.id, m));
    // Pending messages (id = -timestamp) stay last, in send order, until the server confirms them.
    const order = (m: DirectChatMessage) => (m.id < 0 ? 1e15 - m.id : m.id);
    return [...byId.values()].sort((a, b) => order(a) - order(b));
  }

  private markRead(recipient: DirectChatRecipient): void {
    this.api.markRead(recipient.account_id).pipe(takeUntil(this.host.destroy$)).subscribe({
      next: () => {
        recipient.unread = 0;
        this.refreshUnread();
      },
    });
  }

  private refreshUnread(): void {
    this.api.getCategories().pipe(takeUntil(this.host.destroy$)).subscribe({
      next: (categories) => this.applyCategories(categories),
    });
  }

  private applyCategories(categories: DirectChatCategory[]): void {
    this.categories = categories;
    this.unreadTotal = categories.reduce((sum, c) => sum + (c.unread || 0), 0);
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.host.messagesContainer();
      if (el) el.scrollTop = el.scrollHeight;
    }, 0);
  }
}
